import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Upload, FileText, Copy, Check, CheckCircle, Circle,
  ChevronRight, RefreshCcw, ThumbsUp, 
  AlertCircle, Loader2, X, FolderOpen, Download, FileSearch,
  Trash2, Pencil, Mail, ChevronDown
} from 'lucide-react';
import { MobileLayout, MobileSheet } from './ui';
import { API_ROOT } from '@/config';
import useSubscription from '@/hooks/useSubscription';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface Campaign {
  campaign_uuid: string;
  campaign_name: string;
  brand_name: string;
  created_at: string;
  submissions_count: number;
  submissions?: Submission[];
  requirements_json?: string;
}

interface Submission {
  analysis_id: string;
  creator_name: string;
  creator_first_name?: string;
  creator_surname?: string;
  creator_email: string;
  compliance_score: number;
  created_at: string;
  submitted_at?: string;
  approval_status: string;
  submission_status: string;
  version?: number;
  video_url?: string;
}

interface ExtractedBriefingData {
  campaign_name: string;
  brand_name: string;
  product_name: string;
  hooks: string[];
  hooks_must_be_present: boolean;
  topics: string[];
  topics_must_be_present: boolean;
  call_to_actions: string[];
  call_to_actions_must_be_present: boolean;
  elements: string[];
  elements_must_be_present: boolean;
  story: string;
  story_must_be_present: boolean;
  visuals_background: string;
  visuals_background_must_be_present: boolean;
  brand_logo_path?: string;
  brand_logo_must_be_present: boolean;
  product_image_path?: string;
  product_image_must_be_present: boolean;
}

// Language options for email templates
const LANGUAGES = [
  { value: 'en', label: 'English', flagUrl: 'https://flagcdn.com/w40/us.png' },
  { value: 'de', label: 'Deutsch', flagUrl: 'https://flagcdn.com/w40/de.png' },
  { value: 'fr', label: 'French', flagUrl: 'https://flagcdn.com/w40/fr.png' },
  { value: 'es', label: 'Spanish', flagUrl: 'https://flagcdn.com/w40/es.png' },
  { value: 'pt', label: 'Portuguese', flagUrl: 'https://flagcdn.com/w40/pt.png' },
];

const BrandCampaignManagerMobile: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, refresh: refreshSubscription } = useSubscription();
  
  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [error, setError] = useState('');
  const [createError, setCreateError] = useState('');
  
  // Sheets
  const [showNewCampaignSheet, setShowNewCampaignSheet] = useState(false);
  const [showSubmissionSheet, setShowSubmissionSheet] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  
  // New campaign form - 3-step flow
  const [uploadMode, setUploadMode] = useState<'file' | 'paste'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [campaignStep, setCampaignStep] = useState<'upload' | 'review' | 'published'>('upload');
  const [extractedData, setExtractedData] = useState<ExtractedBriefingData | null>(null);
  const [campaignLink, setCampaignLink] = useState('');
  
  // Copy state
  const [copySuccess, setCopySuccess] = useState(false);
  const [linkCopySuccess, setLinkCopySuccess] = useState(false);

  // Delete/Rename
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRenameSheet, setShowRenameSheet] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Email template state
  const [showEmailSheet, setShowEmailSheet] = useState(false);
  const [emailType, setEmailType] = useState<'approval' | 'revision'>('approval');
  const [emailLanguage, setEmailLanguage] = useState('en');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCopySuccess, setEmailCopySuccess] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // User info for email templates
  const [userInfo, setUserInfo] = useState<any>(null);

  // Fetch user info for email templates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_ROOT}/users/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUserInfo(data); })
      .catch(() => {});
  }, []);

  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      navigate('/');
      return;
    }

    try {
      const response = await fetch(`${API_ROOT}/brand/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        setIsLoading(false);
        navigate('/');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      } else {
        setError(`Failed to load campaigns (${response.status})`);
      }
    } catch (err) {
      setError('Network error: Failed to load campaigns');
    } finally {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadingTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setError('Loading took too long. Please try again.');
    }, 5000);

    fetchCampaigns();

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [fetchCampaigns]);

  // Refresh submissions by re-fetching campaigns
  const fetchSubmissions = async (campaignUuid: string) => {
    setIsLoadingSubmissions(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_ROOT}/brand/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const allCampaigns: Campaign[] = data.campaigns || [];
        setCampaigns(allCampaigns);
        const updated = allCampaigns.find((c) => c.campaign_uuid === campaignUuid);
        if (updated) {
          setSelectedCampaign(updated);
          const visible = (updated.submissions || []).filter(
            (s: Submission) => s.submission_status !== 'discarded'
          );
          setSubmissions(visible);
        }
      }
    } catch (err) {
      console.error('Failed to refresh submissions:', err);
    } finally {
      setIsLoadingSubmissions(false);
    }
  };

  // Select campaign — switches to inline detail view
  const handleSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    const visible = (campaign.submissions || []).filter(
      (s: Submission) => s.submission_status !== 'discarded'
    );
    setSubmissions(visible);
  };

  // Go back — close any open sheet first, then go to campaign list
  const handleBackToList = () => {
    if (showBriefingSheet) {
      setShowBriefingSheet(false);
      return;
    }
    if (showSubmissionSheet) {
      setShowSubmissionSheet(false);
      return;
    }
    setSelectedCampaign(null);
    setSubmissions([]);
  };

  // Copy campaign link
  const handleCopyLink = async () => {
    if (!selectedCampaign) return;
    const link = `${window.location.origin}/upload/${selectedCampaign.campaign_uuid}`;
    await navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // ============ CAMPAIGN CREATION 3-STEP FLOW ============

  // Step 1: Extract briefing
  const handleExtractBriefing = async () => {
    if (!selectedFile && !pastedText.trim()) {
      setCreateError('Please upload a file or paste briefing text');
      return;
    }

    setIsCreating(true);
    setCreateError('');
    const token = localStorage.getItem('token');

    try {
      let response;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        response = await fetch(`${API_ROOT}/extract-briefing`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } else {
        response = await fetch(`${API_ROOT}/extract-briefing-text`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text: pastedText }),
        });
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to process briefing');
      }

      const extracted = await response.json();
      setExtractedData(extracted);
      setCampaignStep('review');
    } catch (err: any) {
      setCreateError(err.message || 'Failed to process briefing');
    } finally {
      setIsCreating(false);
    }
  };

  // Step 2 -> 3: Publish campaign
  const handlePublishCampaign = async () => {
    if (!extractedData) return;

    if (!extractedData.campaign_name?.trim()) {
      setCreateError('Campaign name is required');
      return;
    }

    setIsCreating(true);
    setCreateError('');
    const token = localStorage.getItem('token');

    try {
      // Build structured requirements
      const structuredRequirements: any = {};
      if (extractedData.hooks?.length) structuredRequirements.hooks = extractedData.hooks.filter(h => h.trim());
      if (extractedData.topics?.length) structuredRequirements.topics = extractedData.topics.filter(t => t.trim());
      if (extractedData.call_to_actions?.length) structuredRequirements.call_to_actions = extractedData.call_to_actions.filter(c => c.trim());
      if (extractedData.elements?.length) structuredRequirements.elements = extractedData.elements.filter(e => e.trim());
      if (extractedData.story?.trim()) structuredRequirements.story = extractedData.story;
      if (extractedData.visuals_background?.trim()) structuredRequirements.visuals_background = extractedData.visuals_background;

      // Build flat requirements for backward compat
      const allRequirements: string[] = [
        ...(extractedData.hooks || []),
        ...(extractedData.topics || []),
        ...(extractedData.call_to_actions || []),
        ...(extractedData.elements || []),
      ].filter(r => r.trim());

      if (extractedData.story?.trim()) allRequirements.push(extractedData.story);
      if (extractedData.visuals_background?.trim()) allRequirements.push(extractedData.visuals_background);

      if (allRequirements.length === 0) {
        setCreateError('At least one requirement is needed');
        setIsCreating(false);
        return;
      }

      const createResponse = await fetch(`${API_ROOT}/brand/campaigns`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaign_name: extractedData.campaign_name,
          brand_name: extractedData.brand_name,
          product_name: extractedData.product_name,
          requirements: allRequirements,
          requirements_structured: structuredRequirements,
          brand_logo_path: extractedData.brand_logo_path || null,
          product_image_path: extractedData.product_image_path || null,
        }),
      });

      if (!createResponse.ok) {
        const errData = await createResponse.json();
        throw new Error(errData.detail || 'Failed to create campaign');
      }

      const data = await createResponse.json();
      const link = `${window.location.origin}/upload/${data.campaign_uuid}`;
      setCampaignLink(link);
      setCampaignStep('published');
      fetchCampaigns();
      refreshSubscription();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create campaign');
    } finally {
      setIsCreating(false);
    }
  };

  const resetCampaignCreation = () => {
    setCampaignStep('upload');
    setExtractedData(null);
    setSelectedFile(null);
    setPastedText('');
    setCreateError('');
    setCampaignLink('');
    setLinkCopySuccess(false);
  };

  // Helper to update an array field in extractedData
  const updateArrayField = (field: keyof ExtractedBriefingData, index: number, value: string) => {
    if (!extractedData) return;
    const arr = [...(extractedData[field] as string[])];
    arr[index] = value;
    setExtractedData({ ...extractedData, [field]: arr });
  };

  const addArrayItem = (field: keyof ExtractedBriefingData) => {
    if (!extractedData) return;
    const arr = [...(extractedData[field] as string[]), ''];
    setExtractedData({ ...extractedData, [field]: arr });
  };

  const removeArrayItem = (field: keyof ExtractedBriefingData, index: number) => {
    if (!extractedData) return;
    const arr = (extractedData[field] as string[]).filter((_, i) => i !== index);
    setExtractedData({ ...extractedData, [field]: arr });
  };

  // ============ VIEW SUBMISSION ============

  const handleViewSubmission = (submission: Submission) => {
    setSelectedSubmission(submission);
    setShowSubmissionSheet(true);
  };

  // ============ APPROVE / REVISION WITH EMAIL ============

  const buildApprovalEmail = useCallback((language: string, submission: Submission) => {
    const creatorName = submission.creator_first_name || submission.creator_name || 'Creator';
    const campaignName = selectedCampaign?.campaign_name || 'your campaign';
    const submissionUrl = `${window.location.origin}/campaign-result/${submission.analysis_id}`;
    const companyName = userInfo?.company_name || 'The Brand';
    const signature = `The ${companyName} Team`;

    const templates: Record<string, { subject: string; body: string }> = {
      en: {
        subject: '✅ Your Video Submission has been Approved!',
        body: `Dear ${creatorName},\n\nGreat news! Your video submission for ${campaignName} has been approved.\n\nYou can view your approved submission here:\n${submissionUrl}\n\nThank you for your excellent work!\n\nBest regards,\n${signature}`,
      },
      de: {
        subject: '✅ Deine Videoeinreichung wurde genehmigt!',
        body: `Liebe/r ${creatorName},\n\nTolle Neuigkeiten! Deine Videoeinreichung für ${campaignName} wurde genehmigt.\n\nDu kannst deine genehmigte Einreichung hier ansehen:\n${submissionUrl}\n\nVielen Dank für deine großartige Arbeit!\n\nMit freundlichen Grüßen,\n${signature}`,
      },
      fr: {
        subject: '✅ Votre soumission vidéo a été approuvée !',
        body: `Cher/Chère ${creatorName},\n\nBonne nouvelle ! Votre soumission vidéo pour ${campaignName} a été approuvée.\n\nVous pouvez consulter votre soumission approuvée ici :\n${submissionUrl}\n\nMerci pour votre excellent travail !\n\nCordialement,\n${signature}`,
      },
      es: {
        subject: '✅ Tu envío de video ha sido aprobado',
        body: `Estimado/a ${creatorName},\n\n¡Buenas noticias! Tu envío de video para ${campaignName} ha sido aprobado.\n\nPuedes ver tu envío aprobado aquí:\n${submissionUrl}\n\n¡Gracias por tu excelente trabajo!\n\nSaludos cordiales,\n${signature}`,
      },
      pt: {
        subject: '✅ Sua submissão de vídeo foi aprovada',
        body: `Prezado/a ${creatorName},\n\nÓtimas notícias! Sua submissão de vídeo para ${campaignName} foi aprovada.\n\nVocê pode ver sua submissão aprovada aqui:\n${submissionUrl}\n\nObrigado pelo seu excelente trabalho!\n\nAtenciosamente,\n${signature}`,
      },
    };
    return templates[language] || templates.en;
  }, [selectedCampaign, userInfo]);

  const buildRevisionEmail = useCallback((language: string, submission: Submission) => {
    const creatorName = submission.creator_first_name || submission.creator_name || 'Creator';
    const campaignName = selectedCampaign?.campaign_name || 'your campaign';
    const submissionUrl = `${window.location.origin}/campaign-result/${submission.analysis_id}`;
    const campaignUploadUrl = `${window.location.origin}/upload/${selectedCampaign?.campaign_uuid}`;
    const companyName = userInfo?.company_name || 'The Brand';
    const signature = `The ${companyName} Team`;

    const templates: Record<string, { subject: string; body: string }> = {
      en: {
        subject: '🔄 Revision Requested for Your Video Submission',
        body: `Dear ${creatorName},\n\nThank you for your submission for ${campaignName}.\n\nWe would like to request some revisions to better align with our campaign briefing:\n\n>>> PLEASE ADD YOUR SPECIFIC REVISION FEEDBACK HERE <<<\n\nYou can view your submission here:\n${submissionUrl}\n\nTo upload a revised version, please use this link:\n${campaignUploadUrl}\n\nWe look forward to seeing your revised submission!\n\nBest regards,\n${signature}`,
      },
      de: {
        subject: '🔄 Überarbeitung für deine Videoeinreichung angefordert',
        body: `Liebe/r ${creatorName},\n\nVielen Dank für deine Einreichung für ${campaignName}.\n\nWir möchten einige Überarbeitungen anfordern, um sie besser an unser Kampagnen-Briefing anzupassen:\n\n>>> BITTE FÜGE HIER DEIN SPEZIFISCHES FEEDBACK EIN <<<\n\nDu kannst deine Einreichung hier ansehen:\n${submissionUrl}\n\nUm eine überarbeitete Version hochzuladen, nutze bitte diesen Link:\n${campaignUploadUrl}\n\nWir freuen uns auf deine überarbeitete Einreichung!\n\nMit freundlichen Grüßen,\n${signature}`,
      },
      fr: {
        subject: '🔄 Révision demandée pour votre soumission vidéo',
        body: `Cher/Chère ${creatorName},\n\nMerci pour votre soumission pour ${campaignName}.\n\nNous aimerions demander quelques révisions pour mieux correspondre à notre brief de campagne :\n\n>>> VEUILLEZ AJOUTER VOS COMMENTAIRES SPÉCIFIQUES ICI <<<\n\nVous pouvez consulter votre soumission ici :\n${submissionUrl}\n\nPour télécharger une version révisée, utilisez ce lien :\n${campaignUploadUrl}\n\nNous avons hâte de voir votre soumission révisée !\n\nCordialement,\n${signature}`,
      },
      es: {
        subject: '🔄 Revisión solicitada para tu envío de video',
        body: `Estimado/a ${creatorName},\n\nGracias por tu envío para ${campaignName}.\n\nNos gustaría solicitar algunas revisiones para alinearlo mejor con nuestro briefing de campaña:\n\n>>> POR FAVOR AÑADE TUS COMENTARIOS ESPECÍFICOS AQUÍ <<<\n\nPuedes ver tu envío aquí:\n${submissionUrl}\n\nPara subir una versión revisada, usa este enlace:\n${campaignUploadUrl}\n\n¡Esperamos ver tu envío revisado!\n\nSaludos cordiales,\n${signature}`,
      },
      pt: {
        subject: '🔄 Revisão solicitada para sua submissão de vídeo',
        body: `Prezado/a ${creatorName},\n\nObrigado pela sua submissão para ${campaignName}.\n\nGostaríamos de solicitar algumas revisões para melhor alinhar com nosso briefing de campanha:\n\n>>> POR FAVOR ADICIONE SEU FEEDBACK ESPECÍFICO AQUI <<<\n\nVocê pode ver sua submissão aqui:\n${submissionUrl}\n\nPara enviar uma versão revisada, use este link:\n${campaignUploadUrl}\n\nEstamos ansiosos para ver sua submissão revisada!\n\nAtenciosamente,\n${signature}`,
      },
    };
    return templates[language] || templates.en;
  }, [selectedCampaign, userInfo]);

  // Approve submission, then show email template
  const handleApprove = async (submission: Submission) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_ROOT}/approve-submission`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis_id: submission.analysis_id }),
      });
      if (res.ok && selectedCampaign) {
        fetchSubmissions(selectedCampaign.campaign_uuid);
        // Show email template
        setEmailType('approval');
        setEmailLanguage('en');
        const template = buildApprovalEmail('en', submission);
        setEmailSubject(template.subject);
        setEmailBody(template.body);
        setEmailCopySuccess(false);
        setShowEmailSheet(true);
        setShowSubmissionSheet(false);
      }
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  // Request revision, then show email template
  const handleRevision = async (submission: Submission) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_ROOT}/request-revision`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis_id: submission.analysis_id }),
      });
      if (res.ok && selectedCampaign) {
        fetchSubmissions(selectedCampaign.campaign_uuid);
        // Show email template
        setEmailType('revision');
        setEmailLanguage('en');
        const template = buildRevisionEmail('en', submission);
        setEmailSubject(template.subject);
        setEmailBody(template.body);
        setEmailCopySuccess(false);
        setShowEmailSheet(true);
        setShowSubmissionSheet(false);
      }
    } catch (err) {
      console.error('Failed to request revision:', err);
    }
  };

  // Update email on language change
  const handleEmailLanguageChange = (lang: string) => {
    setEmailLanguage(lang);
    setShowLanguageDropdown(false);
    if (!selectedSubmission) return;
    if (emailType === 'approval') {
      const template = buildApprovalEmail(lang, selectedSubmission);
      setEmailSubject(template.subject);
      setEmailBody(template.body);
    } else {
      const template = buildRevisionEmail(lang, selectedSubmission);
      setEmailSubject(template.subject);
      setEmailBody(template.body);
    }
    setEmailCopySuccess(false);
  };

  const handleCopyEmail = async () => {
    const text = `Subject: ${emailSubject}\n\n${emailBody}`;
    await navigator.clipboard.writeText(text);
    setEmailCopySuccess(true);
    setTimeout(() => setEmailCopySuccess(false), 2000);
  };

  const handleOpenInEmail = () => {
    if (!selectedSubmission) return;
    const mailto = `mailto:${selectedSubmission.creator_email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(mailto);
  };

  // ============ VIDEO DOWNLOAD ============

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadVideo = async (analysisId: string) => {
    setIsDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const urlRes = await fetch(`${API_ROOT}/video/${analysisId}/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!urlRes.ok) throw new Error('Failed to get download URL');

      const { presigned_url } = await urlRes.json();
      if (!presigned_url) { alert('No download URL available.'); return; }

      const blobRes = await fetch(presigned_url);
      if (!blobRes.ok) throw new Error('Failed to fetch video');
      const blob = await blobRes.blob();

      const filename = presigned_url.split('/').pop()?.split('?')[0] || `video_${analysisId}.mp4`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download video. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // ============ DELETE CAMPAIGN ============

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    setIsDeleting(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_ROOT}/brand/campaigns/${selectedCampaign.campaign_uuid}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete campaign');

      setCampaigns(prev => prev.filter(c => c.campaign_uuid !== selectedCampaign.campaign_uuid));
      setShowDeleteConfirm(false);
      setSelectedCampaign(null);
      setSubmissions([]);
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ============ RENAME CAMPAIGN ============

  const handleRenameCampaign = async () => {
    if (!selectedCampaign || !newCampaignName.trim()) return;
    setIsRenaming(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_ROOT}/brand/campaigns/${selectedCampaign.campaign_uuid}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_name: newCampaignName.trim() }),
      });
      if (!response.ok) throw new Error('Failed to rename campaign');

      setCampaigns(prev =>
        prev.map(c =>
          c.campaign_uuid === selectedCampaign.campaign_uuid
            ? { ...c, campaign_name: newCampaignName.trim() }
            : c
        )
      );
      setSelectedCampaign({ ...selectedCampaign, campaign_name: newCampaignName.trim() });
      setShowRenameSheet(false);
    } catch (err) {
      console.error('Failed to rename campaign:', err);
      alert('Failed to rename campaign. Please try again.');
    } finally {
      setIsRenaming(false);
    }
  };

  // View briefing state
  const [showBriefingSheet, setShowBriefingSheet] = useState(false);
  const [briefingContent, setBriefingContent] = useState<any>(null);

  // Refs for browser back button handling (avoids stale closures)
  const briefingSheetRef = useRef(false);
  const submissionSheetRef = useRef(false);
  const sheetHistoryRef = useRef(false);

  useEffect(() => { briefingSheetRef.current = showBriefingSheet; }, [showBriefingSheet]);
  useEffect(() => { submissionSheetRef.current = showSubmissionSheet; }, [showSubmissionSheet]);

  // Single persistent popstate handler — uses refs so it always reads latest state
  useEffect(() => {
    const onPopState = () => {
      if (briefingSheetRef.current) {
        sheetHistoryRef.current = false;
        setShowBriefingSheet(false);
      } else if (submissionSheetRef.current) {
        sheetHistoryRef.current = false;
        setShowSubmissionSheet(false);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Push a history entry when a sheet opens; clean up when closed via X button
  useEffect(() => {
    const anyOpen = showBriefingSheet || showSubmissionSheet;
    if (anyOpen && !sheetHistoryRef.current) {
      window.history.pushState({ sheet: true }, '');
      sheetHistoryRef.current = true;
    } else if (!anyOpen && sheetHistoryRef.current) {
      sheetHistoryRef.current = false;
      window.history.back();
    }
  }, [showBriefingSheet, showSubmissionSheet]);

  const handleViewBriefing = () => {
    if (!selectedCampaign) return;
    setShowBriefingSheet(true);
    try {
      const requirements = JSON.parse(selectedCampaign.requirements_json || '{}');
      setBriefingContent(requirements);
    } catch (err) {
      console.error('Failed to parse briefing:', err);
      setBriefingContent(null);
    }
  };

  // Get score color — matches desktop brand-campaign-manager.tsx
  const getScoreColor = (score: number) => {
    if (score >= 90) return { bg: PALETTE.inchworm, text: PALETTE.gunmetal };
    if (score >= 61) return { bg: '#fbbf24', text: PALETTE.gunmetal };
    return { bg: '#fca5a5', text: PALETTE.gunmetal };
  };

  // Get display status for a submission
  const getDisplayStatus = (submission: Submission) => {
    if (submission.approval_status === 'approved') return { label: 'Approved', color: PALETTE.gunmetal, bg: PALETTE.inchworm };
    if (submission.approval_status === 'revision_requested') return { label: 'In Revision', color: PALETTE.gunmetal, bg: PALETTE.orange };
    if (submission.submission_status === 'submitted') return { label: 'Submitted', color: '#2563eb', bg: 'rgba(37,99,235,0.1)' };
    if (submission.submission_status === 'pending') return { label: 'Pending', color: '#666', bg: '#f0f0f0' };
    const s = submission.submission_status || 'pending';
    return { label: s.charAt(0).toUpperCase() + s.slice(1), color: '#666', bg: '#f0f0f0' };
  };

  // ============ RENDER ARRAY EDIT SECTION ============
  const renderArrayEditor = (label: string, field: keyof ExtractedBriefingData) => {
    if (!extractedData) return null;
    const items = (extractedData[field] as string[]) || [];
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal }}>{label} ({items.length})</label>
          <button
            onClick={() => addArrayItem(field)}
            style={{
              background: 'none',
              border: 'none',
              color: PALETTE.inchworm,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Plus size={14} /> Add
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input
              className="mobile-input"
              value={item}
              onChange={(e) => updateArrayField(field, i, e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              onClick={() => removeArrayItem(field, i)}
              style={{
                background: 'none',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                padding: '0 10px',
                color: '#999',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <MobileLayout
      title={selectedCampaign ? selectedCampaign.campaign_name : "Campaigns"}
      showBack={!!selectedCampaign}
      onBack={handleBackToList}
    >
      <SEO
        title="Campaign Manager"
        description="Manage your influencer and UGC campaigns. Create briefs, track creator submissions, and review video compliance."
        path="/brand/campaign"
      />
      <div style={{ padding: 16 }}>
        {/* ============ CAMPAIGN LIST VIEW ============ */}
        {!selectedCampaign && (
          <>
            {/* Usage Warning */}
            {subscription && !subscription.can_analyze && (
              <div style={{
                background: 'rgba(254, 119, 51, 0.1)',
                border: '1px solid rgba(254, 119, 51, 0.3)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <AlertCircle size={20} color={PALETTE.orange} />
                  <span style={{ fontWeight: 600, color: PALETTE.orange }}>Usage Limit Reached</span>
                </div>
                <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
                  You've used all {subscription.monthly_limit} video checks this month.
                </p>
                <button
                  className="mobile-btn mobile-btn-secondary"
                  style={{ marginTop: 12, padding: '10px 16px' }}
                  onClick={() => navigate('/subscription')}
                >
                  Upgrade Plan
                </button>
              </div>
            )}

            {/* Create Campaign Button */}
            <button
              className="mobile-btn mobile-btn-primary mobile-btn-full"
              onClick={() => {
                resetCampaignCreation();
                setShowNewCampaignSheet(true);
              }}
              style={{ marginBottom: 24 }}
            >
              <Plus size={20} style={{ marginRight: 8 }} />
              Create New Campaign
            </button>

            {/* Error with Retry */}
            {error && !isLoading && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
                textAlign: 'center',
              }}>
                <p style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{error}</p>
                <button
                  onClick={() => {
                    setError('');
                    setIsLoading(true);
                    fetchCampaigns();
                  }}
                  style={{
                    background: PALETTE.inchworm,
                    color: PALETTE.gunmetal,
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 24px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Campaigns List */}
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div className="mobile-spinner" style={{ margin: '0 auto' }} />
                <p style={{ marginTop: 16, color: '#666' }}>Loading campaigns...</p>
              </div>
            ) : campaigns.length === 0 && !error ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <FolderOpen size={48} color="#ccc" style={{ marginBottom: 16 }} />
                <h3 style={{ color: PALETTE.gunmetal, marginBottom: 8 }}>No campaigns yet</h3>
                <p style={{ color: '#666', fontSize: 14 }}>
                  Create your first campaign to get started
                </p>
              </div>
            ) : (
              <div className="mobile-list" style={{ borderRadius: 12, overflow: 'hidden' }}>
                {campaigns.map((campaign) => (
                  <button
                    key={campaign.campaign_uuid}
                    className="mobile-list-item"
                    onClick={() => handleSelectCampaign(campaign)}
                    style={{ 
                      width: '100%', 
                      textAlign: 'left', 
                      background: 'none', 
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="mobile-list-item-content">
                      <div className="mobile-list-item-title">{campaign.campaign_name}</div>
                      <div className="mobile-list-item-subtitle">
                        {campaign.brand_name} • {campaign.submissions_count || 0} submissions
                      </div>
                    </div>
                    <ChevronRight size={20} className="mobile-list-item-action" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ============ CAMPAIGN DETAIL VIEW (inline, replaces list) ============ */}
        {selectedCampaign && (
          <>
            {/* Campaign Actions */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <button
                onClick={handleCopyLink}
                style={{
                  flex: 1,
                  background: copySuccess ? PALETTE.inchworm : PALETTE.gunmetal,
                  color: copySuccess ? PALETTE.gunmetal : '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 0',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                {copySuccess ? <Check size={16} /> : <Copy size={16} />}
                {copySuccess ? 'Copied!' : 'Copy Link'}
              </button>
              <button
                onClick={handleViewBriefing}
                style={{
                  background: 'transparent',
                  border: `2px solid ${PALETTE.gunmetal}`,
                  color: PALETTE.gunmetal,
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <FileSearch size={16} />
                Briefing
              </button>
            </div>

            {/* Rename & Delete row */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button
                onClick={() => {
                  setNewCampaignName(selectedCampaign.campaign_name);
                  setShowRenameSheet(true);
                }}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: '1px solid #e0e0e0',
                  color: PALETTE.gunmetal,
                  borderRadius: 12,
                  padding: '10px 0',
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <Pencil size={14} />
                Rename
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#dc2626',
                  borderRadius: 12,
                  padding: '10px 0',
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>

            {/* Submissions List */}
            <h3 style={{ fontSize: 16, marginBottom: 12, color: PALETTE.gunmetal }}>
              Submissions ({submissions.length})
            </h3>

            {isLoadingSubmissions ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div className="mobile-spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : submissions.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40, 
                background: '#f9f9f9',
                borderRadius: 12 
              }}>
                <p style={{ color: '#666', margin: 0 }}>No submissions yet</p>
                <p style={{ color: '#999', fontSize: 13, marginTop: 4 }}>
                  Share the link with creators to receive submissions
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {submissions.map((submission) => {
                  const scoreColor = getScoreColor(submission.compliance_score);
                  const status = getDisplayStatus(submission);
                  return (
                    <button
                      key={submission.analysis_id}
                      onClick={() => handleViewSubmission(submission)}
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        padding: 16,
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      {/* Score Badge */}
                      <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: scoreColor.bg,
                        color: scoreColor.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 18,
                        flexShrink: 0,
                      }}>
                        {Math.round(submission.compliance_score)}%
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: PALETTE.gunmetal }}>
                          {submission.creator_first_name && submission.creator_surname
                            ? `${submission.creator_first_name} ${submission.creator_surname}`
                            : submission.creator_name}
                        </div>
                        {submission.submission_status !== 'pending' && submission.creator_email && (
                          <div style={{ fontSize: 13, color: '#666' }}>
                            {submission.creator_email}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: '#999' }}>
                            {new Date(submission.submitted_at || submission.created_at).toLocaleDateString()}
                          </span>
                          {/* Version badge */}
                          {submission.submission_status === 'submitted' && submission.version && (
                            <span style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: PALETTE.gunmetal,
                              background: `${PALETTE.americanSilver}40`,
                              padding: '2px 8px',
                              borderRadius: 99,
                            }}>V{submission.version}</span>
                          )}
                          {/* Status badge */}
                          <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: status.color,
                            background: status.bg,
                            padding: '2px 8px',
                            borderRadius: 99,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}>
                            {submission.approval_status === 'approved' && (
                              <CheckCircle size={12} style={{ color: PALETTE.inchworm, flexShrink: 0 }} />
                            )}
                            {submission.approval_status === 'revision_requested' && (
                              <Circle size={12} style={{ color: PALETTE.orange, fill: PALETTE.orange, flexShrink: 0 }} />
                            )}
                            {status.label}
                          </span>
                        </div>
                      </div>

                      <ChevronRight size={20} color="#999" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ============ NEW CAMPAIGN SHEET (3-STEP) ============ */}
      <MobileSheet
        isOpen={showNewCampaignSheet}
        onClose={() => {
          setShowNewCampaignSheet(false);
          resetCampaignCreation();
        }}
        title={
          campaignStep === 'upload' ? 'Create Campaign' :
          campaignStep === 'review' ? 'Review & Edit' :
          'Campaign Created!'
        }
        fullScreen={campaignStep === 'review'}
      >
        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, padding: '0 4px' }}>
          {['Upload', 'Review', 'Link'].map((label, idx) => {
            const stepIndex = ['upload', 'review', 'published'].indexOf(campaignStep);
            const isActive = stepIndex === idx;
            const isCompleted = stepIndex > idx;
            return (
              <React.Fragment key={label}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    background: isActive ? PALETTE.gunmetal : isCompleted ? PALETTE.inchworm : '#e0e0e0',
                    color: isActive ? '#fff' : PALETTE.gunmetal,
                  }}>
                    {isCompleted ? <Check size={14} /> : idx + 1}
                  </div>
                  <span style={{ fontSize: 11, color: isActive ? PALETTE.gunmetal : '#999', fontWeight: isActive ? 600 : 400 }}>
                    {label}
                  </span>
                </div>
                {idx < 2 && (
                  <div style={{
                    flex: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    paddingBottom: 18,
                  }}>
                    <div style={{
                      height: 2,
                      width: '100%',
                      background: isCompleted ? PALETTE.inchworm : '#e0e0e0',
                      borderRadius: 1,
                    }} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1: UPLOAD */}
        {campaignStep === 'upload' && (
          <>
            <div className="mobile-tabs" style={{ marginBottom: 20 }}>
              <button
                className={`mobile-tab ${uploadMode === 'file' ? 'active' : ''}`}
                onClick={() => setUploadMode('file')}
              >
                <Upload size={16} style={{ marginRight: 6 }} />
                Upload File
              </button>
              <button
                className={`mobile-tab ${uploadMode === 'paste' ? 'active' : ''}`}
                onClick={() => setUploadMode('paste')}
              >
                <FileText size={16} style={{ marginRight: 6 }} />
                Paste Text
              </button>
            </div>

            {createError && (
              <div style={{ 
                background: '#fef2f2', 
                padding: 12, 
                borderRadius: 8, 
                marginBottom: 16,
                color: '#dc2626',
                fontSize: 14
              }}>
                {createError}
              </div>
            )}

            {uploadMode === 'file' ? (
              <div 
                className={`mobile-upload-zone ${selectedFile ? 'active' : ''}`}
                onClick={() => document.getElementById('mobile-file-input')?.click()}
              >
                <input
                  id="mobile-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  style={{ display: 'none' }}
                />
                <Upload size={48} className="mobile-upload-zone-icon" />
                {selectedFile ? (
                  <>
                    <p className="mobile-upload-zone-text">{selectedFile.name}</p>
                    <p className="mobile-upload-zone-hint">Tap to change file</p>
                  </>
                ) : (
                  <>
                    <p className="mobile-upload-zone-text">Tap to upload briefing</p>
                    <p className="mobile-upload-zone-hint">PDF, DOC, DOCX, or TXT</p>
                  </>
                )}
              </div>
            ) : (
              <textarea
                className="mobile-input"
                placeholder="Paste your campaign briefing here..."
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                style={{ minHeight: 200, resize: 'none' }}
              />
            )}

            <div style={{
              position: 'sticky',
              bottom: 0,
              paddingTop: 16,
              paddingBottom: 8,
              background: 'white',
              marginTop: 12,
              zIndex: 1,
            }}>
              <button
                className="mobile-btn mobile-btn-primary mobile-btn-full"
                onClick={handleExtractBriefing}
                disabled={isCreating || (!selectedFile && !pastedText.trim())}
              >
                {isCreating ? (
                  <>
                    <Loader2 size={20} className="mobile-spinner" style={{ width: 20, height: 20, marginRight: 8 }} />
                    Extracting...
                  </>
                ) : (
                  'Extract Requirements'
                )}
              </button>
            </div>
          </>
        )}

        {/* STEP 2: REVIEW & EDIT */}
        {campaignStep === 'review' && extractedData && (
          <div style={{ paddingBottom: 80 }}>
            {createError && (
              <div style={{ 
                background: '#fef2f2', 
                padding: 12, 
                borderRadius: 8, 
                marginBottom: 16,
                color: '#dc2626',
                fontSize: 14
              }}>
                {createError}
              </div>
            )}

            {/* Campaign Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 6 }}>
                Campaign Name <span style={{ color: PALETTE.orange }}>*</span>
              </label>
              <input
                className="mobile-input"
                value={extractedData.campaign_name}
                onChange={(e) => setExtractedData({ ...extractedData, campaign_name: e.target.value })}
                placeholder="Campaign name"
              />
            </div>

            {/* Brand & Product */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 6 }}>
                  Brand Name
                </label>
                <input
                  className="mobile-input"
                  value={extractedData.brand_name}
                  onChange={(e) => setExtractedData({ ...extractedData, brand_name: e.target.value })}
                  placeholder="Brand name"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 6 }}>
                  Product Name
                </label>
                <input
                  className="mobile-input"
                  value={extractedData.product_name}
                  onChange={(e) => setExtractedData({ ...extractedData, product_name: e.target.value })}
                  placeholder="Product name"
                />
              </div>
            </div>

            {/* Requirement categories */}
            {renderArrayEditor('Hooks', 'hooks')}
            {renderArrayEditor('Topics', 'topics')}
            {renderArrayEditor('Call to Actions', 'call_to_actions')}
            {renderArrayEditor("Don'ts", 'elements')}

            {/* Story */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 6 }}>
                Story
              </label>
              <textarea
                className="mobile-input"
                value={extractedData.story || ''}
                onChange={(e) => setExtractedData({ ...extractedData, story: e.target.value })}
                placeholder="Story description..."
                style={{ minHeight: 80, resize: 'none' }}
              />
            </div>

            {/* Visuals & Background */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 6 }}>
                Visuals & Background
              </label>
              <textarea
                className="mobile-input"
                value={extractedData.visuals_background || ''}
                onChange={(e) => setExtractedData({ ...extractedData, visuals_background: e.target.value })}
                placeholder="Visual requirements..."
                style={{ minHeight: 80, resize: 'none' }}
              />
            </div>

            {/* Sticky publish button */}
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 16,
              background: 'white',
              borderTop: '1px solid #e0e0e0',
              zIndex: 10,
              display: 'flex',
              gap: 12,
            }}>
              <button
                className="mobile-btn mobile-btn-outline"
                onClick={() => { setCampaignStep('upload'); setCreateError(''); }}
                style={{ flex: 1 }}
              >
                Back
              </button>
              <button
                className="mobile-btn mobile-btn-primary"
                onClick={handlePublishCampaign}
                disabled={isCreating}
                style={{ flex: 2 }}
              >
                {isCreating ? (
                  <>
                    <Loader2 size={20} className="mobile-spinner" style={{ width: 20, height: 20, marginRight: 8 }} />
                    Publishing...
                  </>
                ) : (
                  'Create Submission Link'
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: PUBLISHED - SHOW LINK */}
        {campaignStep === 'published' && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: `${PALETTE.inchworm}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Check size={32} color={PALETTE.gunmetal} />
            </div>

            <h3 style={{ color: PALETTE.gunmetal, marginBottom: 8 }}>
              Campaign Created!
            </h3>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
              Share this link with your creators to submit videos
            </p>

            {/* Campaign info */}
            {extractedData && (
              <div style={{
                background: '#f9f9f9',
                borderRadius: 12,
                padding: 16,
                marginBottom: 20,
                textAlign: 'left',
              }}>
                <p style={{ fontWeight: 600, color: PALETTE.gunmetal, margin: '0 0 4px' }}>
                  {extractedData.campaign_name}
                </p>
                {extractedData.brand_name && (
                  <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
                    {extractedData.brand_name}
                  </p>
                )}
              </div>
            )}

            {/* Link box */}
            <div style={{
              background: '#f9f9f9',
              border: '2px solid #e0e0e0',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              wordBreak: 'break-all',
              fontSize: 13,
              color: PALETTE.gunmetal,
              textAlign: 'left',
            }}>
              {campaignLink}
            </div>

            <button
              className="mobile-btn mobile-btn-primary mobile-btn-full"
              onClick={async () => {
                await navigator.clipboard.writeText(campaignLink);
                setLinkCopySuccess(true);
                setTimeout(() => setLinkCopySuccess(false), 2000);
              }}
              style={{ marginBottom: 12 }}
            >
              {linkCopySuccess ? (
                <><Check size={18} style={{ marginRight: 8 }} /> Copied!</>
              ) : (
                <><Copy size={18} style={{ marginRight: 8 }} /> Copy Submission Link</>
              )}
            </button>

            <button
              className="mobile-btn mobile-btn-outline mobile-btn-full"
              onClick={() => {
                setShowNewCampaignSheet(false);
                resetCampaignCreation();
              }}
            >
              Done
            </button>
          </div>
        )}
      </MobileSheet>

      {/* ============ SUBMISSION DETAIL SHEET ============ */}
      <MobileSheet
        isOpen={showSubmissionSheet}
        onClose={() => setShowSubmissionSheet(false)}
        title="Submission Details"
        fullScreen
      >
        {selectedSubmission && (
          <div>
            {/* Score */}
            <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 20 }}>
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  margin: '0 auto',
                  background: selectedSubmission.compliance_score >= 90
                    ? PALETTE.inchworm
                    : selectedSubmission.compliance_score >= 61
                    ? '#fbbf24'
                    : '#fca5a5',
                }}
              >
                <span style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: PALETTE.gunmetal,
                }}>{Math.round(selectedSubmission.compliance_score)}%</span>
              </div>
            </div>

            {/* Creator Info */}
            <div style={{
              background: '#f9f9f9',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>Creator</h4>
              <p style={{ margin: 0, fontWeight: 600, color: PALETTE.gunmetal }}>
                {selectedSubmission.creator_first_name && selectedSubmission.creator_surname
                  ? `${selectedSubmission.creator_first_name} ${selectedSubmission.creator_surname}`
                  : selectedSubmission.creator_name}
              </p>
              {selectedSubmission.submission_status !== 'pending' && selectedSubmission.creator_email && (
                <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
                  {selectedSubmission.creator_email}
                </p>
              )}
              <p style={{ margin: '8px 0 0', color: '#999', fontSize: 13 }}>
                Submitted {new Date(selectedSubmission.submitted_at || selectedSubmission.created_at).toLocaleString()}
              </p>
              {/* Version & Status badges */}
              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selectedSubmission.submission_status === 'submitted' && selectedSubmission.version && (
                  <span style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: PALETTE.gunmetal,
                    background: `${PALETTE.americanSilver}40`,
                    padding: '4px 12px',
                    borderRadius: 99,
                  }}>Version {selectedSubmission.version}</span>
                )}
                {(() => {
                  const status = getDisplayStatus(selectedSubmission);
                  return (
                    <span style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: status.color,
                      background: status.bg,
                      padding: '4px 12px',
                      borderRadius: 99,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}>
                      {selectedSubmission.approval_status === 'approved' && (
                        <CheckCircle size={14} style={{ color: PALETTE.inchworm, flexShrink: 0 }} />
                      )}
                      {selectedSubmission.approval_status === 'revision_requested' && (
                        <Circle size={14} style={{ color: PALETTE.orange, fill: PALETTE.orange, flexShrink: 0 }} />
                      )}
                      {status.label}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Actions — only for submitted analyses, not pending ones */}
            {selectedSubmission.submission_status === 'submitted' ? (
              <>
                {/* Primary Actions */}
                {(() => {
                  const isDecided = selectedSubmission.approval_status === 'approved' || selectedSubmission.approval_status === 'revision_requested';
                  return (
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                      <button
                        onClick={() => handleApprove(selectedSubmission)}
                        style={{
                          flex: 1,
                          background: PALETTE.inchworm,
                          color: PALETTE.gunmetal,
                          border: 'none',
                          borderRadius: 12,
                          padding: '12px 0',
                          fontSize: 14,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          cursor: isDecided ? 'default' : 'pointer',
                          opacity: isDecided ? 0.5 : 1,
                        }}
                        disabled={isDecided}
                      >
                        <ThumbsUp size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => handleRevision(selectedSubmission)}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          color: PALETTE.orange,
                          border: `2px solid ${PALETTE.orange}`,
                          borderRadius: 12,
                          padding: '12px 0',
                          fontSize: 14,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          cursor: isDecided ? 'default' : 'pointer',
                          opacity: isDecided ? 0.5 : 1,
                        }}
                        disabled={isDecided}
                      >
                        <RefreshCcw size={16} />
                        Revision
                      </button>
                    </div>
                  );
                })()}

                {/* Video download + view result */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <button
                    onClick={() => handleDownloadVideo(selectedSubmission.analysis_id)}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: `1px solid #e0e0e0`,
                      color: PALETTE.gunmetal,
                      borderRadius: 12,
                      padding: '12px 0',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      cursor: 'pointer',
                    }}
                  >
                    {isDownloading ? <Loader2 size={16} className="mobile-spinner" style={{ width: 16, height: 16 }} /> : <Download size={16} />}
                    {isDownloading ? 'Downloading...' : 'Download Video'}
                  </button>
                  <button
                    onClick={() => navigate(`/campaign-result/${selectedSubmission.analysis_id}?campaign=${selectedCampaign?.campaign_uuid || ''}`)}
                    style={{
                      flex: 1,
                      background: PALETTE.gunmetal,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      padding: '12px 0',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      cursor: 'pointer',
                    }}
                  >
                    View Full Result
                  </button>
                </div>
              </>
            ) : (
              <div style={{
                background: '#f9f9f9',
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
                textAlign: 'center',
              }}>
                <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
                  This video was analyzed but not yet submitted by the creator.
                </p>
              </div>
            )}
          </div>
        )}
      </MobileSheet>

      {/* ============ EMAIL TEMPLATE SHEET ============ */}
      <MobileSheet
        isOpen={showEmailSheet}
        onClose={() => setShowEmailSheet(false)}
        title={emailType === 'approval' ? 'Approval Email' : 'Revision Email'}
        fullScreen
      >
        <div>
          {/* Language selector */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 6 }}>Language</label>
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              style={{
                width: '100%',
                background: '#f9f9f9',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                padding: '12px 16px',
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                color: PALETTE.gunmetal,
              }}
            >
              <img
                src={LANGUAGES.find(l => l.value === emailLanguage)?.flagUrl}
                alt=""
                style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }}
              />
              <span style={{ flex: 1, textAlign: 'left' }}>
                {LANGUAGES.find(l => l.value === emailLanguage)?.label}
              </span>
              <ChevronDown size={16} color="#999" />
            </button>

            {showLanguageDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                zIndex: 20,
                marginTop: 4,
                overflow: 'hidden',
              }}>
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.value}
                    onClick={() => handleEmailLanguageChange(lang.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: lang.value === emailLanguage ? '#f9f9f9' : '#fff',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      color: PALETTE.gunmetal,
                    }}
                  >
                    <img src={lang.flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }} />
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 6 }}>Subject</label>
            <input
              className="mobile-input"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </div>

          {/* Body */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 6 }}>Body</label>
            <textarea
              className="mobile-input"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              style={{ minHeight: 200, resize: 'none', fontFamily: 'inherit', lineHeight: 1.6 }}
            />
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button
              onClick={handleCopyEmail}
              style={{
                flex: 1,
                background: emailCopySuccess ? PALETTE.inchworm : PALETTE.gunmetal,
                color: emailCopySuccess ? PALETTE.gunmetal : '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '12px 0',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              {emailCopySuccess ? <Check size={16} /> : <Copy size={16} />}
              {emailCopySuccess ? 'Copied!' : 'Copy Text'}
            </button>
            <button
              onClick={handleOpenInEmail}
              style={{
                flex: 1,
                background: 'transparent',
                border: `2px solid ${PALETTE.gunmetal}`,
                color: PALETTE.gunmetal,
                borderRadius: 12,
                padding: '12px 0',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              <Mail size={16} />
              Open in Email
            </button>
          </div>

          <button
            className="mobile-btn mobile-btn-outline mobile-btn-full"
            onClick={() => setShowEmailSheet(false)}
          >
            Done
          </button>
        </div>
      </MobileSheet>

      {/* ============ BRIEFING DETAIL SHEET ============ */}
      <MobileSheet
        isOpen={showBriefingSheet}
        onClose={() => setShowBriefingSheet(false)}
        title="Campaign Briefing"
        fullScreen
      >
        {briefingContent ? (
          <div>
            {(() => {
              if (Array.isArray(briefingContent)) {
                const validReqs = briefingContent.filter((r: any) => r && typeof r === 'string' && r.trim());
                if (validReqs.length === 0) {
                  return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
                }
                return (
                  <div style={{ background: `${PALETTE.americanSilver}10`, borderRadius: 12, padding: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                      All Requirements ({validReqs.length})
                    </h4>
                    {validReqs.map((req: string, idx: number) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <span style={{ flexShrink: 0, marginTop: 7, width: 4, height: 4, borderRadius: '50%', background: PALETTE.gunmetal, display: 'inline-block' }} />
                        <span style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal }}>{req}</span>
                      </div>
                    ))}
                  </div>
                );
              }

              if (typeof briefingContent === 'object' && briefingContent !== null) {
                const categoryDisplayNames: { [key: string]: string } = {
                  'hooks': 'Hooks',
                  'topics': 'Topics',
                  'call_to_actions': 'Call to Actions',
                  'elements': "Don'ts",
                  'story': 'Story',
                  'visuals_background': 'Visuals & Background'
                };
                const categoryOrder = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
                const categories = categoryOrder
                  .map(key => ({ key, value: briefingContent[key] }))
                  .filter(({ value }) => {
                    if (Array.isArray(value)) return value.some((item: any) => item && typeof item === 'string' && item.trim());
                    if (typeof value === 'string') return value.trim().length > 0;
                    return false;
                  });

                if (categories.length === 0) {
                  return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
                }

                return (
                  <div>
                    {categories.map(({ key, value }) => {
                      const displayName = categoryDisplayNames[key] || key;
                      const isTextCategory = key === 'story' || key === 'visuals_background';
                      return (
                        <div key={key} style={{ background: `${PALETTE.americanSilver}10`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
                          {Array.isArray(value) ? (
                            <>
                              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                                {displayName} ({value.filter((i: any) => i && i.trim()).length})
                              </h4>
                              {value.filter((item: any) => item && item.trim()).map((item: string, idx: number) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                                  <span style={{ flexShrink: 0, marginTop: 7, width: 4, height: 4, borderRadius: '50%', background: PALETTE.gunmetal, display: 'inline-block' }} />
                                  <span style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal }}>{item}</span>
                                </div>
                              ))}
                            </>
                          ) : typeof value === 'string' && isTextCategory ? (
                            <>
                              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                                {displayName}
                              </h4>
                              <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal, margin: 0 }}>
                                {value}
                              </p>
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
            })()}
          </div>
        ) : (
          <p style={{ color: '#666', textAlign: 'center', padding: 40 }}>Could not load briefing.</p>
        )}
      </MobileSheet>

      {/* ============ DELETE CONFIRMATION OVERLAY ============ */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 360,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px', color: PALETTE.gunmetal }}>Delete Campaign?</h3>
            <p style={{ color: '#666', fontSize: 14, margin: '0 0 20px' }}>
              This will permanently delete <strong>{selectedCampaign?.campaign_name}</strong> and all its submissions. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  background: '#f0f0f0',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 0',
                  fontSize: 14,
                  fontWeight: 600,
                  color: PALETTE.gunmetal,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCampaign}
                disabled={isDeleting}
                style={{
                  flex: 1,
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 0',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: 'pointer',
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ RENAME SHEET ============ */}
      <MobileSheet
        isOpen={showRenameSheet}
        onClose={() => setShowRenameSheet(false)}
        title="Rename Campaign"
      >
        <div>
          <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, display: 'block', marginBottom: 8 }}>
            Campaign Name
          </label>
          <input
            className="mobile-input"
            value={newCampaignName}
            onChange={(e) => setNewCampaignName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCampaignName.trim()) handleRenameCampaign();
            }}
            autoFocus
            placeholder="Enter new name"
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              className="mobile-btn mobile-btn-outline"
              onClick={() => setShowRenameSheet(false)}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              className="mobile-btn mobile-btn-primary"
              onClick={handleRenameCampaign}
              disabled={isRenaming || !newCampaignName.trim()}
              style={{ flex: 1, opacity: (isRenaming || !newCampaignName.trim()) ? 0.6 : 1 }}
            >
              {isRenaming ? 'Renaming...' : 'Rename'}
            </button>
          </div>
        </div>
      </MobileSheet>
    </MobileLayout>
  );
};

export default BrandCampaignManagerMobile;
